import { memo } from "react";
import { slug } from "@/lib/geo";

export const Countries = memo(({ shapes, fills, lit, selected, onSelect }) => (
  <g>
    {shapes.map((s) => (
      <path
        key={s.name}
        d={s.d}
        className={`country country-${fills[s.name] || "none"} ${selected === s.name ? "country-selected" : ""}`}
        onClick={() => onSelect(s.name)}
        data-testid={`map-country-${s.slug}`}
      >
        <title>{s.name}</title>
      </path>
    ))}
    {shapes.filter((s) => s.dOverseas).map((s) => (
      <path key={`${s.name}-overseas`} d={s.dOverseas} onClick={() => onSelect(s.name)}
        className={`country ${lit.has(s.name) ? "country-visited" : "country-none"} ${selected === s.name ? "country-selected" : ""}`} data-testid={`map-territory-${s.slug}`}>
        <title>{`Territory of ${s.name}`}</title>
      </path>
    ))}
  </g>
));

// Text grows on screen as you zoom in, but slower than the map itself
const textSize = (base, k) => base / Math.pow(k, 0.8);

export const CountryLabels = memo(({ shapes, k }) => (
  <g className="pointer-events-none">
    {shapes
      .filter((s) => s.area * k * k > 700)
      .map((s) => (
        <text key={s.name} x={s.c[0]} y={s.c[1]} fontSize={textSize(10, k)} className="country-label" textAnchor="middle">
          {s.name}
        </text>
      ))}
  </g>
));

const tier = (k) => (k < 2 ? 1 : k < 4 ? 3 : k < 8 ? 5 : k < 16 ? 7 : 10);

export const CityLayer = memo(({ cities, k, visitedCities, wishCities, visitedCountries, onCity }) => {
  const t = tier(k);
  return (
    <g>
      {cities.map((c) => {
        const isV = visitedCities.has(`${c.n}|${c.c}`);
        const isW = !isV && wishCities.has(`${c.n}|${c.c}`);
        const limit = visitedCountries.has(c.c) ? t + 2 : t;
        if (!isV && !isW && c.r > limit) return null;
        const label = isV || isW || c.r <= t - 1 || k >= 14;
        return (
          <g
            key={`${c.n}|${c.c}`}
            transform={`translate(${c.px},${c.py})`}
            className="city"
            onClick={(e) => { e.stopPropagation(); onCity(c); }}
            data-testid={`map-city-${slug(c.n)}`}
          >
            <title>{`${c.n}, ${c.c}${isV ? " — been here" : isW ? " — want to go" : ""}`}</title>
            <circle r={7 / k} fill="transparent" data-testid={`map-city-hit-${slug(c.n)}`} />
            {isV && <circle r={textSize(12, k)} fill="url(#neon-halo)" className="pointer-events-none" />}
            {isV && <circle r={7 / k} className="city-ring" />}
            <circle r={(isV ? 4.2 : isW ? 3.4 : 2) / k} className={isV ? "city-dot-visited" : isW ? "city-dot-wish" : "city-dot"} strokeWidth={0.7 / k} />
            {label && (
              <text x={(isV ? 6 : 3.5) / k + textSize(1, k)} y={textSize(isV ? 3.5 : 3, k)} fontSize={textSize(isV || isW ? 10.5 : 8.5, k)} className={isV ? "city-label-visited" : isW ? "city-label-wish" : "city-label"}>
                {c.n}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
});

// Friends sharing their city; several in one city fan out slightly
export const FriendPins = memo(({ pins, k }) => {
  const seen = {};
  return (
    <g data-testid="friend-pins">
      {pins.map((p) => {
        const n = (seen[`${p.city}|${p.country}`] = (seen[`${p.city}|${p.country}`] || 0) + 1) - 1;
        const r = 7 / Math.pow(k, 0.85);
        return (
          <g key={p.friend_id} transform={`translate(${p.px + n * r * 1.6},${p.py - r * 1.4})`} className="pointer-events-none" data-testid={`friend-pin-${p.friend_id}`}>
            <title>{`${p.name} is in ${p.city}`}</title>
            <circle r={r} fill="#38BDF8" stroke="#0B0F17" strokeWidth={r * 0.18} />
            <text textAnchor="middle" y={r * 0.38} fontSize={r * 1.05} fontWeight="700" fill="#04121c">{p.name[0]}</text>
            {n === 0 && <text x={r * 1.4} y={r * 0.35} fontSize={textSize(9.5, k)} className="friend-pin-label">{`${p.name.split(" ")[0]} · ${p.city}`}</text>}
          </g>
        );
      })}
    </g>
  );
});


// Your own current city — only drawn on your screen
export const MePin = memo(({ me, k }) => {
  const r = 6 / Math.pow(k, 0.85);
  return (
    <g transform={`translate(${me.px},${me.py})`} className="pointer-events-none" data-testid="me-pin">
      <title>{`You're in ${me.city}`}</title>
      <circle r={r * 2.4} className="me-pin-pulse" fill="#34D399" opacity="0.25" />
      <circle r={r} fill="#34D399" stroke="#ECFDF5" strokeWidth={r * 0.35} />
      <text x={r * 1.6} y={-r * 1.3} fontSize={textSize(10, k)} className="me-pin-label">{`You · ${me.city}`}</text>
    </g>
  );
});
